// Branch charges page - the plain parts (tested alone): the period presets, the customer search, and what the
// page says about each line / kind / portal gap.
import { formatContractDateTime } from '../../utils/contractDateUtils';
import { contractDateSheetCell } from '../../utils/excelDates';
import { dubaiIsoDay } from '../../utils/dubaiTime';

const KIND_WORD = { fine: 'Fines', toll: 'Salik', darb: 'Darb', parking: 'Parking' };

/**
 * A line's one state (the engine's `state`, owner 28 Sep 2026) and the badge the page gives it: "On Ejar" only for
 * what Ejar really holds - a Salik / Darb trip covered by Unlimited Salik is not charged and not on Ejar either.
 * `variant` is the state's ONE colour everywhere on the page - the line badge AND the words in the kind cell
 * (kindParts' tone): green = on Ejar, purple = covered by Unlimited Salik (never the blue of the "Open" badge),
 * red = still to charge, grey = paid by the customer at RTA.
 */
export const LINE_STATES = {
    on_ejar: { label: 'On Ejar', variant: 'success' },
    unlimited: { label: 'Unlimited Salik', variant: 'accent' },
    not_on_ejar: { label: 'Not on Ejar', variant: 'danger' },
    customer_paid: { label: 'Paid by customer at RTA', variant: 'neutral' },
    // Ejar holds it at ANOTHER amount (owner, 1 Oct 2026 - line by line, exact): a fine, or ONE line for a
    // "Salik till" line whose amount is not the gate + 1.05 of its trips - short (still owed) or overcharged
    amount_mismatch: { label: 'Amount differs on Ejar', variant: 'warning' },
};

/** The state of a line; an engine from before the states is read from its old fields (never "on Ejar" by guess). */
export function lineState(x) {
    if (x && LINE_STATES[x.state]) return x.state;
    if (x?.paidByCustomer) return 'customer_paid';
    if (x?.note === 'covered by Unlimited Salik') return 'unlimited';
    return x?.onEjar ? 'on_ejar' : 'not_on_ejar';
}

/**
 * The words after a kind's count, from the engine's per-state counts: [{ key, text, tone }] - tone is the state's
 * colour (LINE_STATES[key].variant), the same as its line badge. "all on Ejar" ONLY when every line is really on Ejar
 * (owner, 28 Sep 2026: Invygo's Salik said "all on Ejar" while Ejar held nothing).
 */
export function kindParts(k) {
    if (!k || !k.n) return [];
    const notOn = k.notOnEjarN || 0;
    const part = (key, text) => ({ key, text, tone: LINE_STATES[key].variant });
    // an engine from before the states only says what is owed: nothing more is claimed
    if (k.onEjarN === undefined) return notOn ? [part('not_on_ejar', `${notOn} not on Ejar`)] : [];
    // a Salik line check / the Salik total that differs is no trip (the engine's `n` leaves it out): never "all on Ejar"
    if (k.onEjarN === k.n && !k.mismatchN) return [part('on_ejar', 'all on Ejar')];
    if (k.unlimitedN === k.n && !k.mismatchN) return [part('unlimited', 'covered by Unlimited Salik')];
    return [
        notOn && part('not_on_ejar', `${notOn} not on Ejar`),
        k.onEjarN && part('on_ejar', `${k.onEjarN} on Ejar`),
        k.unlimitedN && part('unlimited', `${k.unlimitedN} Unlimited Salik`),
        k.customerPaidN && part('customer_paid', `${k.customerPaidN} paid by customer`),
        k.mismatchN && part('amount_mismatch', `${k.mismatchN} amount differs on Ejar`),
    ].filter(Boolean);
}

/**
 * The empty table's words. "Not on Ejar only" keeps the lines still to charge ONLY, so an empty table there says just
 * that - never "every line is on Ejar": the period may hold no line at all, or only fines the customer paid at RTA.
 */
export function emptyText({ search = '', pick = '', notOnEjar = false } = {}) {
    if (search || pick) return 'No customer matches.';
    return notOnEjar ? 'Nothing left to charge in this period.' : 'No charges for this branch in this period.';
}

/** A kind as one text - "3 · 2 not on Ejar · 1 on Ejar" (the copied table); '' when it has no line. */
export const kindText = (k) => (k && k.n ? [String(k.n), ...kindParts(k).map((p) => p.text)].join(' · ') : '');

/** A summary card's line: the same words, the part not on Ejar as its amount ("12 · 30.30 not on Ejar · ..."). */
export function kindCardText(k, fmt = String) {
    if (!k || !k.n) return 'none';
    return [String(k.n), ...kindParts(k).map((p) => (p.key === 'not_on_ejar' ? `${fmt(k.notOnEjar)} not on Ejar` : p.text))].join(' · ');
}

/**
 * The portal warnings, in words the owner understands (owner, 28 Sep 2026). The test server (the engine's
 * `server: 'staging'`) never pulls the Salik / Darb / parking portals: one plain notice instead of one per kind.
 */
export function coverageNotices(report) {
    const missing = Object.entries(report?.coverageMissing || {}).filter(([, days]) => days && days.length);
    if (!missing.length) return [];
    if (report?.server === 'staging') {
        const which = missing.map(([k, days]) => `${KIND_WORD[k] || k} ${days.length} day(s)`).join(', ');
        return [`Test server: it does not pull the Salik, Darb and parking portals, so trips after its copy of the data are missing here (${which}). The live server has them.`];
    }
    return missing.map(([k, days]) => `${KIND_WORD[k] || k}: nothing was pulled from the portal for ${days.length} day(s) of this period (${days.slice(0, 6).join(', ')}${days.length > 6 ? '…' : ''}) - trips of those days are missing from this report.`);
}

const pad = (n) => String(n).padStart(2, '0');
/** A local calendar day as YYYY-MM-DD. */
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export const PRESETS = [
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This week' },
    { id: 'last7', label: 'Last 7 days' },
    { id: 'month', label: 'This month' },
    { id: 'lastMonth', label: 'Last month' },
    { id: 'custom', label: 'Dates…' },
];

/** The period of a preset as {from, to} (YYYY-MM-DD, both included). The week starts on Monday. */
export function periodOf(preset, today = new Date()) {
    // today's DUBAI day (as a local date, so the date maths below stays simple), whatever the browser's zone
    const [y, m, d] = dubaiIsoDay(today).split('-').map(Number);
    const t = new Date(y, m - 1, d);
    switch (preset) {
        case 'today': return { from: ymd(t), to: ymd(t) };
        case 'week': return { from: ymd(addDays(t, -((t.getDay() + 6) % 7))), to: ymd(t) };
        case 'last7': return { from: ymd(addDays(t, -6)), to: ymd(t) };
        case 'month': return { from: ymd(new Date(t.getFullYear(), t.getMonth(), 1)), to: ymd(t) };
        case 'lastMonth': return { from: ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: ymd(new Date(t.getFullYear(), t.getMonth(), 0)) };
        case 'yesterday':
        default: { const y = addDays(t, -1); return { from: ymd(y), to: ymd(y) }; }
    }
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "ALWFQ-0041" -> the next invoice number keeping the prefix and the zero padding ("ALWFQ-0042"). */
export function nextInvoiceNo(no, step = 1) {
    const m = /^(.*?)(\d+)$/.exec(String(no || '').trim());
    if (!m) return String(no || '');
    return `${m[1]}${String(parseInt(m[2], 10) + step).padStart(m[2].length, '0')}`;
}

/**
 * Invygo invoices (the old Matcher Salik / Parking summaries), one per contract of the report:
 * kind 'salik' = Salik + Darb trips, 'parking' = parking. Numbered from `first` (ALWFQ-n, padding kept),
 * the model from the contracts store (`byContract` = Map(contract -> row)) and the booking number = Invygo's own
 * booking id from `invygoIds` (src/services/invygoIds.js: Map(CONTRACT NO -> { invygoId })) - empty when it is not
 * known, never Ejar's own booking number: Invygo files its invoices by its id (owner, 28 Sep 2026).
 */
export function invygoInvoices(rows, byContract, { kind = 'salik', first = 'ALWFQ-1', date = '', invygoIds = null } = {}) {
    const kinds = kind === 'parking' ? ['parking'] : ['toll', 'darb'];
    const out = [];
    (rows || []).forEach((r) => {
        const lines = (r.lines || []).filter((x) => kinds.includes(x.kind));
        if (!lines.length) return;
        const c = (byContract && byContract.get(r.contract)) || {};
        const [y, mo] = lines[0].when.slice(0, 10).split('-').map(Number);
        out.push({
            invoice: nextInvoiceNo(first, out.length) || first,
            customer: r.customer || c.Customer || '',
            booking: (invygoIds && invygoIds.get(String(r.contract || '').trim().toUpperCase()))?.invygoId || '',
            contract: r.contract,
            // the Invygo car's model: a contract on a yelo car now (car_now_at - contractsV2.carNowOf) has that car's
            // model in Model (4 Nov 2026), which was never Invygo's car
            model: (c.car_now_at && c['Invygo Model']) || c.Model || '',
            plate: (r.plates || []).join(', '),
            // the contract's start / end with the time: "01/09/2026 10:00" (owner, 27 Sep 2026)
            start: r.start ? (formatContractDateTime(r.start) || r.start.slice(0, 10)) : '',
            end: r.isOpen ? 'Open' : (r.end ? (formatContractDateTime(r.end) || r.end.slice(0, 10)) : ''),
            // the engine's own values behind them: the Excel writes real date cells from these
            startRaw: r.start || '',
            endRaw: r.isOpen ? '' : (r.end || ''),
            month: y && mo ? `${MONTHS[mo - 1]} ${y}` : '',
            trips: lines.length,
            total: Math.round(lines.reduce((a, x) => a + x.charge, 0) * 100) / 100,
            date,
        });
    });
    return out;
}

/**
 * The Excel rows of the Invygo invoices (`cols` = [[key, header]]): the contract's start / end as real
 * Excel dates shown dd/mm/yyyy hh:mm, so Invygo can sort and filter them by date (as "dd/mm/yyyy" text
 * 01/10 sorted before 02/09); "Open" and a start that cannot be read stay the text shown on the page.
 */
export function invygoSheetRows(invoices, cols) {
    return (invoices || []).map((x) => Object.fromEntries(cols.map(([k, h]) => {
        if (k === 'start' || k === 'end') return [h, x[`${k}Raw`] ? contractDateSheetCell(x[`${k}Raw`], x[k]) : x[k]];
        return [h, x[k]];
    })));
}

/** Customers matching a typed text (contract, customer name, plate); every word must be found. */
export function matchRows(rows, text) {
    const words = String(text || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return rows || [];
    return (rows || []).filter((r) => {
        const hay = `${r.contract} ${r.customer} ${(r.plates || []).join(' ')} ${(r.plates || []).join(' ').replace(/\s+/g, '')}`.toLowerCase();
        return words.every((w) => hay.includes(w));
    });
}
